import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plant, Cow, Drop, Globe, Leaf, Factory } from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import { IMAGES, INTEGRATED_MODEL } from "../data";

const VALUES = [
  { title: "Traceability", body: "Every batch traceable to a soil block, a herd, and a feed lot." },
  { title: "No Outsourcing", body: "Zero milk procurement from third-party farmers. Ever." },
  { title: "Animal Welfare", body: "Open grazing, ample fodder, veterinary oversight on payroll." },
  { title: "Farmer Empowerment", body: "Training programs and input supply for partner growers." },
];

const LEADERSHIP = [
  { name: "[Founder Name]", role: "Chairperson & Managing Director" },
  { name: "[Co-Founder Name]", role: "Director — Operations & Cattle" },
  { name: "[CTO Name]", role: "Director — Processing & Quality" },
  { name: "[CFO Name]", role: "Director — Finance & Trade" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About AxonPremium"
        title={
          <>
            Built like an institution.
            <br />
            <span className="italic text-terracotta">Run like a farm.</span>
          </>
        }
        subtitle="A new generation Indian agri-dairy enterprise — engineered around vertical integration, pharmaceutical-grade inputs, and export-ready process discipline."
        image={IMAGES.field}
        testId="about-hero"
      />

      {/* Vision / Mission */}
      <section className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36" data-testid="about-vision-mission">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-6">
            <span className="overline">Vision</span>
            <h2 className="h-section text-3xl md:text-4xl mt-4">
              To become the most trusted source of vertically integrated dairy for
              institutional buyers and exporters across emerging markets.
            </h2>
          </div>
          <div className="lg:col-span-6">
            <span className="overline">Mission</span>
            <p className="mt-4 text-olive-soft text-lg leading-relaxed">
              Replace the fragmented, aggregation-driven dairy model with a
              closed-loop, single-origin supply chain that delivers
              pharmaceutical-grade inputs, batch-level traceability, and
              consistent quality at scale.
            </p>
          </div>
        </div>
      </section>

      {/* Integrated Model expanded */}
      <section className="bg-sand py-24 lg:py-36" data-testid="about-integrated-model">
        <div className="max-w-8xl mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <span className="overline">The Integrated Model · Expanded</span>
            <h2 className="h-section text-4xl md:text-5xl mt-6 max-w-3xl">
              Six closed-loop stages. One owner.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
            {INTEGRATED_MODEL.map((s, i) => {
              const Icon = [Plant, Leaf, Cow, Drop, Factory, Globe][i];
              return (
                <div key={s.step} className="flex gap-6" data-testid={`about-stage-${s.step}`}>
                  <div className="shrink-0">
                    <Icon size={36} weight="duotone" className="text-terracotta" />
                    <div className="mt-3 font-mono text-xs tracking-widest2 text-olive-soft">
                      STEP {s.step}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl md:text-3xl text-olive-deep">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-olive-soft leading-relaxed">{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Land & Farms */}
      <section className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36" data-testid="about-land">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline">Our Land & Farms</span>
            <h2 className="h-section text-4xl md:text-5xl mt-6">
              Soil first. <span className="italic text-terracotta">Always.</span>
            </h2>
            <p className="mt-8 text-olive-soft leading-relaxed">
              Our owned acreage spans rich alluvial belts across Gujarat. Plots
              are dedicated to Moringa, multi-cut fodder grasses, and pulses —
              each block coded and lot-tracked through to the cattle they feed.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            <img src={IMAGES.field} alt="Farmland" className="w-full h-72 object-cover" />
            <img src={IMAGES.moringa} alt="Moringa cultivation" className="w-full h-72 object-cover mt-12" />
            <img src={IMAGES.herd} alt="Premium herd" className="w-full h-72 object-cover" />
            <img src={IMAGES.facility} alt="Logistics" className="w-full h-72 object-cover mt-12" />
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-olive text-bone py-24 lg:py-32" data-testid="about-leadership">
        <div className="max-w-8xl mx-auto px-6 lg:px-12">
          <span className="overline text-terracotta">Leadership</span>
          <h2 className="font-heading text-4xl md:text-5xl mt-6 max-w-3xl">
            A board built for scale & global compliance.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 mt-16">
            {LEADERSHIP.map((p, i) => (
              <div
                key={i}
                className="bg-olive p-8 min-h-[220px] flex flex-col justify-end"
                data-testid={`leadership-${i}`}
              >
                <div className="overline text-bone/60 mb-3">0{i + 1}</div>
                <h3 className="font-heading text-2xl text-bone">{p.name}</h3>
                <p className="mt-2 text-sm text-bone/70">{p.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36" data-testid="about-values">
        <span className="overline">Our Values</span>
        <h2 className="h-section text-4xl md:text-5xl mt-6 max-w-3xl">
          Non-negotiables, by design.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {VALUES.map((v, i) => (
            <div
              key={v.title}
              className="card-flat p-8 min-h-[220px] flex flex-col justify-between"
              data-testid={`about-value-${i}`}
            >
              <span className="font-mono text-xs tracking-widest2 text-terracotta">
                0{i + 1}
              </span>
              <div>
                <h3 className="font-heading text-2xl text-olive-deep">{v.title}</h3>
                <p className="mt-3 text-sm text-olive-soft leading-relaxed">{v.body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 flex justify-start">
          <Link to="/contact" className="btn-primary" data-testid="about-cta-contact">
            Talk to Our Trade Team
            <ArrowUpRight size={14} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
